import { useState, useRef } from 'react';
import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { Tag } from '../types';
import { tagsApi } from '../api/tags';
import { contrastColour } from '../utils/helpers';

export default function Tags() {
  const qc = useQueryClient();
  const listRef = useRef<HTMLDivElement>(null);
  const [newName, setNewName] = useState('');
  const [newColour, setNewColour] = useState('#C8102E');
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState('');
  const [editColour, setEditColour] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);
  const deleteRefs = useRef<Record<number, HTMLButtonElement | null>>({});

  const { data } = useQuery({ queryKey: ['tags'], queryFn: () => tagsApi.list() });
  const tags = data?.tags ?? [];

  // Row entrance
  useGSAP(() => {
    if (!listRef.current) return;
    const rows = listRef.current.querySelectorAll('.tag-row');
    gsap.from(rows, { x: -16, opacity: 0, stagger: 0.04, duration: 0.35, ease: 'power3.out' });
  }, [tags.length]);

  const createMutation = useMutation({
    mutationFn: () => tagsApi.create(newName.trim(), newColour),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tags'] }); setNewName(''); setNewColour('#C8102E'); },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, name, colour }: { id: number; name: string; colour: string }) =>
      tagsApi.update(id, { name, colour }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tags'] }); setEditingId(null); },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: number) => tagsApi.delete(id),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['tags'] }); setDeleteConfirm(null); },
  });

  const startEdit = (tag: Tag) => {
    setEditingId(tag.id);
    setEditName(tag.name);
    setEditColour(tag.colour);
  };

  const confirmDelete = (id: number) => {
    setDeleteConfirm(id);
    const btn = deleteRefs.current[id];
    if (btn) gsap.fromTo(btn, { x: -6 }, { x: 0, duration: 0.4, ease: 'elastic.out(1,0.3)' });
  };

  return (
    <div className="min-h-screen sakura-bg">
      <div className="max-w-2xl mx-auto px-4 py-8 space-y-6">
        <h1 className="font-heading text-3xl tracking-wide" style={{ color: 'var(--text)' }}>Release Tags</h1>

        {/* Create new tag */}
        <div className="border px-5 py-5" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
          <h2 className="font-heading text-sm uppercase tracking-wide mb-4" style={{ color: 'var(--text-muted)' }}>Create Tag</h2>
          <div className="flex gap-3 items-end">
            <div className="flex-1">
              <label className="label">Tag Name</label>
              <input
                className="input"
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. B Class Update"
                onKeyDown={e => e.key === 'Enter' && newName.trim() && createMutation.mutate()}
              />
            </div>
            <div>
              <label className="label">Colour</label>
              <div className="flex gap-2 items-center">
                <input
                  type="color"
                  value={newColour}
                  onChange={e => setNewColour(e.target.value)}
                  className="h-9 w-14 cursor-pointer border p-0.5" style={{ borderColor: 'var(--border-strong)' }}
                />
                <span className="font-body text-xs" style={{ color: 'var(--text-muted)' }}>{newColour}</span>
              </div>
            </div>
            <button
              onClick={() => createMutation.mutate()}
              disabled={!newName.trim() || createMutation.isPending}
              className="px-5 py-2 bg-dark text-white text-sm font-heading uppercase hover:bg-primary transition-colors disabled:opacity-40"
            >
              Create
            </button>
          </div>
          {/* Preview */}
          {newName && (
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs font-body" style={{ color: 'var(--text-muted)' }}>Preview:</span>
              <span
                className="inline-block pill px-2 py-0.5 text-xs font-body"
                style={{ background: newColour, color: contrastColour(newColour) }}
              >
                {newName}
              </span>
            </div>
          )}
        </div>

        {/* Tag list */}
        <div ref={listRef} className="space-y-2">
          {tags.length === 0 && (
            <div className="border px-5 py-8 text-center" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
              <p className="font-heading text-xl" style={{ color: 'var(--text-faint)' }}>No tags yet</p>
              <p className="font-body text-sm mt-1" style={{ color: 'var(--text-muted)' }}>Create your first release tag above.</p>
            </div>
          )}

          {tags.map(tag => (
            <div key={tag.id} className="tag-row border px-5 py-3 flex items-center gap-4" style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
              {/* Colour swatch */}
              <div className="w-8 h-8 flex-shrink-0 border" style={{ background: tag.colour, borderColor: 'var(--border)' }} />

              {editingId === tag.id ? (
                /* Edit mode */
                <>
                  <input
                    className="input flex-1 text-sm"
                    value={editName}
                    onChange={e => setEditName(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && updateMutation.mutate({ id: tag.id, name: editName, colour: editColour })}
                    autoFocus
                  />
                  <input
                    type="color"
                    value={editColour}
                    onChange={e => setEditColour(e.target.value)}
                    className="h-8 w-12 cursor-pointer border p-0.5" style={{ borderColor: 'var(--border-strong)' }}
                  />
                  <button
                    onClick={() => updateMutation.mutate({ id: tag.id, name: editName, colour: editColour })}
                    className="px-3 py-1.5 bg-dark text-white text-xs font-heading uppercase hover:bg-primary transition-colors"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="px-3 py-1.5 border text-xs font-heading uppercase transition-colors" style={{ borderColor: 'var(--border-strong)', color: 'var(--text)' }}
                  >
                    Cancel
                  </button>
                </>
              ) : (
                /* View mode */
                <>
                  <div className="flex-1 flex items-center gap-3">
                    <span
                      className="inline-block pill px-2.5 py-0.5 text-sm font-body"
                      style={{ background: tag.colour, color: contrastColour(tag.colour) }}
                    >
                      {tag.name}
                    </span>
                    <span className="text-xs font-body" style={{ color: 'var(--text-muted)' }}>
                      {tag.vehicle_count ?? 0} vehicle{(tag.vehicle_count ?? 0) !== 1 ? 's' : ''}
                    </span>
                  </div>

                  <button
                    onClick={() => startEdit(tag)}
                    className="px-3 py-1.5 border text-xs font-heading uppercase transition-colors" style={{ borderColor: 'var(--border-strong)', color: 'var(--text)' }}
                  >
                    Edit
                  </button>

                  {deleteConfirm === tag.id ? (
                    <div className="flex items-center gap-2">
                      <span className="text-primary text-xs font-body">Delete?</span>
                      <button
                        ref={el => { deleteRefs.current[tag.id] = el; }}
                        onClick={() => deleteMutation.mutate(tag.id)}
                        className="px-3 py-1.5 bg-primary text-white text-xs font-heading uppercase hover:bg-primary-dark transition-colors"
                      >
                        Yes, Delete
                      </button>
                      <button
                        onClick={() => setDeleteConfirm(null)}
                        className="px-2 py-1.5 border text-xs font-heading uppercase transition-colors" style={{ borderColor: 'var(--border-strong)', color: 'var(--text)' }}
                      >
                        Cancel
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => confirmDelete(tag.id)}
                      className="px-3 py-1.5 border border-primary/40 text-primary text-xs font-heading uppercase hover:bg-primary hover:text-white transition-colors"
                    >
                      Delete
                    </button>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
